import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Avatar,
  Grid,
  Alert,
  Snackbar,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  Stack,
  Skeleton,
  Divider,
} from '@mui/material';
import { CURRENCY_CODE } from '../utils/currency';
import PageHeader from '../components/PageHeader';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  QuestionAnswer as QuestionIcon,
  Verified as VerifiedIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useAppData } from '../context/AppDataContext';
import { trainersAPI, gymsAPI } from '../services/api';

const Trainers = () => {
  const { user } = useAppData();
  const isSuperadmin = user?.role === 'superadmin';
  const [trainers, setTrainers] = useState([]);
  const [gyms, setGyms] = useState([]);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Answer dialog state
  const [answerDialog, setAnswerDialog] = useState({ open: false, query: null });
  const [answerContent, setAnswerContent] = useState('');
  const [answerPublic, setAnswerPublic] = useState(false);
  const [answerSubmitting, setAnswerSubmitting] = useState(false);

  // View query detail state
  const [viewDialog, setViewDialog] = useState({ open: false, query: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, trainerId: null });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    specialization: 'workout',
    experience: 0,
    bio: '',
    expertise: [],
    languages: [],
    responseTime: 'within_24_hours',
    consultationFee: 0,
    gym: '',
    isActive: true,
    isVerified: false,
    certifications: []
  });

  const specializations = [
    { value: 'yoga', label: 'Yoga' },
    { value: 'workout', label: 'Workout Training' },
    { value: 'stress_management', label: 'Stress Management' },
    { value: 'nutrition', label: 'Nutrition' },
    { value: 'cardio', label: 'Cardio Training' },
    { value: 'strength', label: 'Strength Training' },
    { value: 'flexibility', label: 'Flexibility & Mobility' },
    { value: 'rehabilitation', label: 'Rehabilitation' }
  ];

  const responseTimeOptions = [
    { value: 'within_1_hour', label: 'Within 1 hour' },
    { value: 'within_4_hours', label: 'Within 4 hours' },
    { value: 'within_24_hours', label: 'Within 24 hours' },
    { value: 'within_48_hours', label: 'Within 48 hours' }
  ];

  useEffect(() => {
    fetchTrainers();
    fetchQueries();
    if (isSuperadmin) fetchGyms();
  }, []);

  const fetchGyms = async () => {
    try {
      const response = await gymsAPI.getAll();
      setGyms(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error fetching gyms:', error);
    }
  };

  const fetchTrainers = async () => {
    try {
      const response = await trainersAPI.getAll();
      setTrainers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching trainers:', error);
      showSnackbar('Failed to fetch trainers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchQueries = async () => {
    try {
      const response = await trainersAPI.getAllQueries();
      setQueries(response.data.data || []);
    } catch (error) {
      console.error('Error fetching queries:', error);
    }
  };

  const openAnswerDialog = (query) => {
    setAnswerContent(query.answer?.content || '');
    setAnswerPublic(query.isPublic || false);
    setAnswerDialog({ open: true, query });
  };

  const handleAnswerSubmit = async () => {
    if (!answerContent.trim()) return;
    setAnswerSubmitting(true);
    try {
      await trainersAPI.answerQuery(answerDialog.query._id, {
        content: answerContent.trim(),
        isPublic: answerPublic,
      });
      showSnackbar('Answer submitted — user notified', 'success');
      setAnswerDialog({ open: false, query: null });
      setAnswerContent('');
      fetchQueries();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to submit answer', 'error');
    } finally {
      setAnswerSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // For non-superadmin users, always tag the trainer with their own gym
      const payload = isSuperadmin
        ? formData
        : { ...formData, gym: user?.gymId || user?.gym || null };

      if (editingTrainer) {
        await trainersAPI.update(editingTrainer._id, payload);
      } else {
        await trainersAPI.create(payload);
      }
      showSnackbar(
        `Trainer ${editingTrainer ? 'updated' : 'created'} successfully`,
        'success'
      );
      setOpenDialog(false);
      resetForm();
      fetchTrainers();
    } catch (error) {
      console.error('Error saving trainer:', error);
      showSnackbar(error.response?.data?.message || error.message, 'error');
    }
  };

  const handleEdit = (trainer) => {
    setEditingTrainer(trainer);
    setFormData({
      name: trainer.name || '',
      email: trainer.email || '',
      phone: trainer.phone || '',
      specialization: trainer.specialization || 'workout',
      experience: trainer.experience || 0,
      bio: trainer.bio || '',
      expertise: trainer.expertise || [],
      languages: trainer.languages || [],
      responseTime: trainer.responseTime || 'within_24_hours',
      consultationFee: trainer.consultationFee || 0,
      gym: trainer.gym?._id || null,
      isActive: trainer.isActive !== false,
      isVerified: trainer.isVerified || false,
      certifications: trainer.certifications || []
    });
    setOpenDialog(true);
  };

  const handleDelete = (trainerId) => {
    setConfirmDialog({ open: true, trainerId });
  };

  const confirmDeactivate = async () => {
    const trainerId = confirmDialog.trainerId;
    setConfirmDialog({ open: false, trainerId: null });
    try {
      await trainersAPI.deactivate(trainerId);
      showSnackbar('Trainer deactivated successfully', 'success');
      fetchTrainers();
    } catch (error) {
      console.error('Error deactivating trainer:', error);
      showSnackbar(error.response?.data?.message || error.message, 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      specialization: 'workout',
      experience: 0,
      bio: '',
      expertise: [],
      languages: [],
      responseTime: 'within_24_hours',
      consultationFee: 0,
      gym: null,
      isActive: true,
      isVerified: false,
      certifications: []
    });
    setEditingTrainer(null);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getSpecializationLabel = (value) => {
    const spec = specializations.find(s => s.value === value);
    return spec ? spec.label : value;
  };

  const getResponseTimeLabel = (value) => {
    const option = responseTimeOptions.find(o => o.value === value);
    return option ? option.label : value;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'answered': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box>
        {/* Header skeleton */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Skeleton width={200} height={32} sx={{ mb: 0.5 }} />
            <Skeleton width={280} height={18} />
          </Box>
          <Skeleton width={120} height={38} sx={{ borderRadius: 2 }} />
        </Stack>
        {/* Stat cards skeleton */}
        <Grid container spacing={2.5} mb={3}>
          {[...Array(4)].map((_, i) => (
            <Grid item xs={6} sm={6} md={3} key={i}>
              <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Skeleton variant="rounded" width={40} height={40} />
                </Stack>
                <Skeleton width={60} height={32} sx={{ mb: 0.5 }} />
                <Skeleton width={100} height={16} />
              </Paper>
            </Grid>
          ))}
        </Grid>
        {/* Table skeleton */}
        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Skeleton width={200} height={36} />
          </Box>
          {[...Array(5)].map((_, i) => (
            <Stack key={i} direction="row" spacing={2} alignItems="center" sx={{ px: 2, py: 1.5, borderBottom: i < 4 ? '1px solid #F3F4F6' : 0 }}>
              <Skeleton variant="circular" width={36} height={36} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="40%" height={14} sx={{ mb: 0.5 }} />
                <Skeleton width="60%" height={12} />
              </Box>
              <Skeleton width={80} height={22} sx={{ borderRadius: 1 }} />
              <Skeleton width={60} height={14} />
              <Skeleton width={70} height={22} sx={{ borderRadius: 1 }} />
              <Skeleton width={60} height={22} sx={{ borderRadius: 1 }} />
              <Skeleton width={64} height={28} sx={{ borderRadius: 1 }} />
            </Stack>
          ))}
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Page header ── */}
      <PageHeader
        title="Trainer Management"
        subtitle="Manage expert trainers and respond to user questions"
        gradient="linear-gradient(90deg, #D97706, #FBBF24)"
        meta={
          <Box sx={{ px: 1, py: 0.25, borderRadius: 1.5, bgcolor: 'action.hover' }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>{trainers.length}</Typography>
          </Box>
        }
        actions={
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => { resetForm(); setOpenDialog(true); }}>Add Trainer</Button>
        }
      />

      {/* ── Stat cards ── */}
      <Grid container spacing={2.5} mb={3}>
        {[
          { label: 'Total Trainers', value: trainers.length,                           color: 'text.primary' },
          { label: 'Active',         value: trainers.filter(t => t.isActive).length,   color: '#16A34A' },
          { label: 'Verified',       value: trainers.filter(t => t.isVerified).length, color: '#0891B2' },
          { label: 'User Questions', value: queries.length,                            color: '#7C3AED' },
        ].map((s) => (
          <Grid item xs={6} sm={6} md={3} key={s.label}>
            <Paper elevation={0} sx={{
              p: 2.5, border: 1, borderColor: 'divider', borderRadius: 2,
              transition: 'box-shadow 0.2s, transform 0.2s',
              '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.08)', transform: 'translateY(-1px)' },
            }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <PeopleIcon sx={{ fontSize: 20, color: s.color }} />
              </Box>
              <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: 'text.primary', lineHeight: 1, mb: 0.5 }}>
                {s.value}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>{s.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── Tabs + Table ── */}
      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Tabs value={selectedTab} onChange={(_, v) => setSelectedTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider', minHeight: 48 }}>
          <Tab label="Trainers" sx={{ minHeight: 48 }} />
          <Tab label={`User Questions${queries.length > 0 ? ` (${queries.length})` : ''}`} sx={{ minHeight: 48 }} />
        </Tabs>

        {/* Trainers Tab */}
        {selectedTab === 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Trainer</TableCell>
                  <TableCell>Specialization</TableCell>
                  <TableCell>Experience</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Questions</TableCell>
                  <TableCell>Response Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trainers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
                      <Box sx={{ textAlign: 'center', py: 10, px: 2 }}>
                        <Box sx={{ width: 72, height: 72, borderRadius: 4, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5 }}>
                          <PeopleIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>No trainers yet</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 300, mx: 'auto' }}>
                          Add your first expert trainer to start managing the team.
                        </Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setOpenDialog(true); }}>
                          Add First Trainer
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : trainers.map((trainer) => (
                  <TableRow key={trainer._id}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar src={trainer.profileImage} sx={{ width: 36, height: 36, fontWeight: 700, fontSize: '0.875rem' }}>
                          {trainer.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>{trainer.name}</Typography>
                            {trainer.isVerified && <VerifiedIcon color="primary" sx={{ fontSize: 14 }} />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">{trainer.email}</Typography>
                          {trainer.gym && <Typography variant="caption" color="text.disabled" display="block">{trainer.gym.name}</Typography>}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip label={getSpecializationLabel(trainer.specialization)} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{trainer.experience} yrs</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <StarIcon sx={{ fontSize: 14, color: '#F59E0B' }} />
                        <Typography variant="body2" fontWeight={600}>{trainer.rating?.average?.toFixed(1) || '0.0'}</Typography>
                        <Typography variant="caption" color="text.secondary">({trainer.rating?.count || 0})</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Badge badgeContent={trainer.totalQuestions || 0} color="primary" max={99}>
                        <QuestionIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Chip label={getResponseTimeLabel(trainer.responseTime)} size="small" icon={<ScheduleIcon />} />
                    </TableCell>
                    <TableCell>
                      <Chip label={trainer.isActive ? 'Active' : 'Inactive'} color={trainer.isActive ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(trainer)} sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}>
                            <EditIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deactivate">
                          <IconButton size="small" onClick={() => handleDelete(trainer._id)} sx={{ color: 'text.secondary', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}>
                            <DeleteIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Queries Tab */}
        {selectedTab === 1 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Trainer</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {queries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
                      <Box sx={{ textAlign: 'center', py: 10 }}>
                        <QuestionIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">No questions submitted yet.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : queries.map((query) => (
                  <TableRow key={query._id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{query.user?.name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{query.user?.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{query.trainer?.name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{query.trainer?.specialization}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap>{query.subject}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={query.category} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label={query.priority} size="small" color={query.priority === 'urgent' ? 'error' : query.priority === 'high' ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Chip label={query.status} size="small" color={getStatusColor(query.status)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{formatDate(query.createdAt)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        <Tooltip title="View question">
                          <IconButton size="small" onClick={() => setViewDialog({ open: true, query })} sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}>
                            <QuestionIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={query.status === 'answered' ? 'Edit answer' : 'Answer'}>
                          <IconButton size="small" color="primary" onClick={() => openAnswerDialog(query)} sx={{ '&:hover': { bgcolor: '#EFF6FF' } }}>
                            <EditIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Deactivate Confirm Dialog ── */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, trainerId: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate Trainer?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will mark the trainer as inactive. They will no longer appear in the app for new queries.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, trainerId: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeactivate}>Deactivate</Button>
        </DialogActions>
      </Dialog>

      {/* ── View Query Dialog ── */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, query: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Question Detail</Typography>
            <IconButton size="small" onClick={() => setViewDialog({ open: false, query: null })}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {viewDialog.query && (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>From</Typography>
                <Typography variant="body2" fontWeight={600}>{viewDialog.query.user?.name}</Typography>
                <Typography variant="caption" color="text.secondary">{viewDialog.query.user?.email}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>To Trainer</Typography>
                <Typography variant="body2" fontWeight={600}>{viewDialog.query.trainer?.name}</Typography>
                <Typography variant="caption" color="text.secondary">{viewDialog.query.trainer?.specialization}</Typography>
              </Paper>
              <Box>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>Subject</Typography>
                <Typography variant="body2" fontWeight={600}>{viewDialog.query.subject}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>Question</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{viewDialog.query.question}</Typography>
              </Box>
              {viewDialog.query.answer?.content && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#BBF7D0', bgcolor: '#F0FDF4' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#16A34A', display: 'block', mb: 0.5 }}>Answer</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{viewDialog.query.answer.content}</Typography>
                  {viewDialog.query.answer.answeredAt && (
                    <Typography variant="caption" color="text.secondary" mt={1} display="block">
                      Answered on {formatDate(viewDialog.query.answer.answeredAt)}
                    </Typography>
                  )}
                </Paper>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {viewDialog.query && viewDialog.query.status !== 'answered' && (
            <Button variant="contained" onClick={() => { setViewDialog({ open: false, query: null }); openAnswerDialog(viewDialog.query); }}>
              Answer this Question
            </Button>
          )}
          <Button onClick={() => setViewDialog({ open: false, query: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Answer Dialog ── */}
      <Dialog open={answerDialog.open} onClose={() => !answerSubmitting && setAnswerDialog({ open: false, query: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{answerDialog.query?.status === 'answered' ? 'Edit Answer' : 'Answer Question'}</Typography>
            <IconButton size="small" onClick={() => !answerSubmitting && setAnswerDialog({ open: false, query: null })}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {answerDialog.query && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, mt: 1 }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Question from {answerDialog.query.user?.name}
              </Typography>
              <Typography variant="body2" fontWeight={600} mb={0.5}>{answerDialog.query.subject}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{answerDialog.query.question}</Typography>
            </Paper>
          )}
          <TextField fullWidth multiline rows={6} label="Your Answer" placeholder="Write a detailed, helpful response..."
            value={answerContent} onChange={(e) => setAnswerContent(e.target.value)} disabled={answerSubmitting} sx={{ mb: 2 }} />
          <FormControlLabel
            control={<Switch checked={answerPublic} onChange={(e) => setAnswerPublic(e.target.checked)} disabled={answerSubmitting} />}
            label="Make this Q&A public (visible to other users)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnswerDialog({ open: false, query: null })} disabled={answerSubmitting}>Cancel</Button>
          <Button variant="contained" onClick={handleAnswerSubmit} disabled={answerSubmitting || !answerContent.trim()}>
            {answerSubmitting ? 'Submitting…' : 'Submit Answer & Notify User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add / Edit Trainer Dialog ── */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{editingTrainer ? 'Edit Trainer' : 'Add New Trainer'}</Typography>
            <IconButton size="small" onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {/* Basic info */}
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>Basic Info</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Email *" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Specialization</InputLabel>
                    <Select value={formData.specialization} onChange={(e) => setFormData({ ...formData, specialization: e.target.value })} label="Specialization">
                      {specializations.map((spec) => <MenuItem key={spec.value} value={spec.value}>{spec.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Bio" multiline rows={3} value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} />
                </Grid>
              </Grid>
            </Paper>

            {/* Professional details */}
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>Professional Details</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Experience (years)" type="number" value={formData.experience} onChange={(e) => setFormData({ ...formData, experience: parseInt(e.target.value) || 0 })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Response Time</InputLabel>
                    <Select value={formData.responseTime} onChange={(e) => setFormData({ ...formData, responseTime: e.target.value })} label="Response Time">
                      {responseTimeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label={`Consultation Fee (${CURRENCY_CODE})`} type="number" value={formData.consultationFee} onChange={(e) => setFormData({ ...formData, consultationFee: parseFloat(e.target.value) || 0 })} />
                </Grid>
                {isSuperadmin && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Gym (optional)</InputLabel>
                      <Select value={formData.gym || ''} onChange={(e) => setFormData({ ...formData, gym: e.target.value || null })} label="Gym (optional)">
                        <MenuItem value=""><em>No gym (global trainer)</em></MenuItem>
                        {gyms.map((gym) => <MenuItem key={gym._id} value={gym._id}>{gym.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Status */}
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>Status</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack direction="row" spacing={3}>
                <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
                <FormControlLabel control={<Switch checked={formData.isVerified} onChange={(e) => setFormData({ ...formData, isVerified: e.target.checked })} />} label="Verified" />
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{editingTrainer ? 'Update Trainer' : 'Create Trainer'}</Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Trainers;
